import {useCallback, useEffect, useRef, useState} from 'react'
import apiClient from '../../helpers/apiClient'

// Infinite scroll para /experiences/search/ cuando NO hay parámetro de búsqueda
// en la URL (modo discovery de /experiences-search). El backend ordena por
// `-created_at` (PR-I-56), así que las páginas devuelven las experiencias más
// recientes primero. LimitOffsetPagination expone `count`/`next`/`results`, así
// que pedimos `limit=pageSize` y avanzamos `offset` hasta que `next` sea null.
// El filtro de categoría se pasa por parámetro; si cambia, el hook resetea la
// paginación (efecto sobre `category`).

export interface UseInfiniteExperiencesSearchResult {
  items: any[]
  loading: boolean
  hasMore: boolean
  error: Error | null
  loadMore: () => void
  /** Ref-callback para el sentinel que dispara la carga al entrar al viewport. */
  sentinelRef: (node: HTMLElement | null) => void
  reset: () => void
}

interface Options {
  /** Si false, el hook no dispara ninguna carga. */
  enabled?: boolean
  /** Cuántos items por página. Default 20 (5 filas × 4 columnas). */
  pageSize?: number
  /**
   * Distancia (px) desde el borde inferior del viewport a la que empieza a
   * cargar la siguiente página. Anticipa la carga para que el usuario no vea
   * el "corte" al final del listado.
   */
  rootMargin?: string
  /**
   * Id de categoría (o `null`) para filtrar el listado sin key/where. Cambia
   * dispara reset + primera carga.
   */
  category?: string | null
}

export const useInfiniteExperiencesSearch = ({
  enabled = true,
  pageSize = 20,
  rootMargin = '400px',
  category = null,
}: Options = {}): UseInfiniteExperiencesSearchResult => {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Refs para leer valores actuales dentro de callbacks sin recrear observers.
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const offsetRef = useRef(0)
  const enabledRef = useRef(enabled)
  const categoryRef = useRef<string | null>(category)

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])
  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])
  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])
  useEffect(() => {
    categoryRef.current = category
  }, [category])

  const reset = useCallback(() => {
    setItems([])
    setLoading(false)
    setHasMore(true)
    setError(null)
    offsetRef.current = 0
    loadingRef.current = false
    hasMoreRef.current = true
  }, [])

  const loadMore = useCallback(async () => {
    if (!enabledRef.current) return
    if (loadingRef.current || !hasMoreRef.current) return

    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const params: Record<string, string | number> = {
        where: '',
        key: '',
        limit: pageSize,
        offset: offsetRef.current,
      }
      const cat = categoryRef.current
      if (cat != null && cat !== '') params.category = cat

      const {data} = await apiClient.get('/experiences/search/', {params})
      const results: any[] = Array.isArray(data?.results) ? data.results : []
      const nextHasMore = Boolean(data?.next) && results.length > 0

      setItems((prev) => {
        // Dedupe por id: race conditions o repeticiones cross-page.
        const seen = new Set(prev.map((m) => String(m?.id)))
        const merged = [...prev]
        for (const m of results) {
          const id = String(m?.id ?? '')
          if (id && !seen.has(id)) {
            seen.add(id)
            merged.push(m)
          }
        }
        return merged
      })
      offsetRef.current += results.length
      hasMoreRef.current = nextHasMore
      setHasMore(nextHasMore)
    } catch (err) {
      setError(err as Error)
      hasMoreRef.current = false
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [pageSize])

  // Reset + primera carga cuando cambia `enabled` o `category`. Usamos
  // `category` como dependencia directa para que al filtrar/deseleccionar
  // el listado reinicie desde el principio.
  useEffect(() => {
    if (!enabled) return
    reset()
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, category])

  // IntersectionObserver con ref-callback para reconectar si el sentinel
  // se re-monta (por ejemplo al alternar entre modo query y discovery).
  const observerRef = useRef<IntersectionObserver | null>(null)
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (!node || !enabled) return
      if (typeof IntersectionObserver === 'undefined') return

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const first = entries[0]
          if (first?.isIntersecting) loadMore()
        },
        {rootMargin}
      )
      observerRef.current.observe(node)
    },
    [enabled, loadMore, rootMargin]
  )

  useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    []
  )

  return {items, loading, hasMore, error, loadMore, sentinelRef, reset}
}
